import React, { useEffect, useMemo, useState } from "react";
import { FileBarChart, Search } from "lucide-react";
import api from "@/lib/api";
import TableExportButton from "@/components/TableExportButton";

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]";
const fmtINR = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ymd = (d) => d.toISOString().slice(0, 10);
const firstOfMonth = () => { const t = new Date(); return ymd(new Date(t.getFullYear(), t.getMonth(), 1)); };
const todayISO = () => ymd(new Date());

const PRESETS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "fy_so_far", label: "FY so far" },
  { key: "all_time", label: "All Time" },
];
const applyPreset = (key) => {
  const t = new Date();
  if (key === "this_month") return { from: firstOfMonth(), to: todayISO() };
  if (key === "last_month") {
    const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const last = new Date(t.getFullYear(), t.getMonth(), 0);
    return { from: ymd(first), to: ymd(last) };
  }
  if (key === "fy_so_far") {
    const fyStartYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return { from: ymd(new Date(fyStartYear, 3, 1)), to: todayISO() };
  }
  if (key === "all_time") return { from: "1970-01-01", to: todayISO() };
  return { from: firstOfMonth(), to: todayISO() };
};

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: firstOfMonth(), to: todayISO() });
  const [presetKey, setPresetKey] = useState("this_month");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (range.from) params.from_date = range.from;
    if (range.to) params.to_date = range.to;
    api.get("/reports/inward-log", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [range.from, range.to]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.cost_center} ${r.supplier_name} ${r.category} ${r.part_no} ${r.part_name}`
        .toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    qty: acc.qty + Number(r.quantity || 0),
    basic: acc.basic + Number(r.basic_value || 0),
    gst: acc.gst + Number(r.gst_value || 0),
    gross: acc.gross + Number(r.gross_value || 0),
  }), { qty: 0, basic: 0, gst: 0, gross: 0 }), [filtered]);

  const exportRows = useMemo(() => filtered.map((r, idx) => ({
    sr_no: idx + 1,
    inward_date: r.inward_date || r.grn_date || r.created_at?.slice(0, 10) || "",
    cost_center: r.cost_center,
    supplier_name: r.supplier_name,
    category: r.category,
    part_no: r.part_no,
    part_name: r.part_name,
    quantity: r.quantity,
    basic_value: r.basic_value,
    gst_value: r.gst_value,
    gross_value: r.gross_value,
  })), [filtered]);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
            <FileBarChart size={12} /> Reports
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Inward Ledger</h1>
          <p className="text-xs text-slate-500 mt-1">
            Cumulative report of GRN'd inwards.
            Rows are grouped by Cost Centre · Supplier · Category · Part No · Part Name —
            if any of those differ, the row is listed separately.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Range</span>
            <select
              value={presetKey}
              onChange={(e) => { const k = e.target.value; setPresetKey(k); if (k !== "custom") setRange(applyPreset(k)); }}
              data-testid="reports-range-preset"
              className="bg-transparent text-sm font-semibold text-slate-900 focus:outline-none">
              {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="custom">Custom…</option>
            </select>
            <input type="date" value={range.from || ""}
              onChange={(e) => { setPresetKey("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
              data-testid="reports-from"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none border-l border-slate-200 pl-2" />
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={range.to || ""}
              onChange={(e) => { setPresetKey("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
              data-testid="reports-to"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none" />
          </div>
          <TableExportButton
            testIdPrefix="reports-export"
            filename="inward_report"
            rows={exportRows}
            dateField="inward_date"
            columns={[
              { key: "sr_no", label: "Sr. No." },
              { key: "inward_date", label: "Date" },
              { key: "cost_center", label: "Cost Centre" },
              { key: "supplier_name", label: "Supplier" },
              { key: "category", label: "Category" },
              { key: "part_no", label: "Part No" },
              { key: "part_name", label: "Part Name" },
              { key: "quantity", label: "Qty" },
              { key: "basic_value", label: "Basic" },
              { key: "gst_value", label: "GST" },
              { key: "gross_value", label: "Gross" },
            ]}
          />
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative w-96">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by cost centre, supplier, category, part…"
            data-testid="reports-search"
            className={`${inputCls} pl-9`} />
        </div>
        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of <span className="font-semibold text-slate-700">{rows.length}</span> grouped rows
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-[#1D2951] text-white text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2.5 text-right w-14">Sr.No.</th>
              <th className="px-3 py-2.5 text-left">Cost Centre</th>
              <th className="px-3 py-2.5 text-left">Supplier Name</th>
              <th className="px-3 py-2.5 text-left">Category</th>
              <th className="px-3 py-2.5 text-left">Part No</th>
              <th className="px-3 py-2.5 text-left">Part Name</th>
              <th className="px-3 py-2.5 text-right">Quantity</th>
              <th className="px-3 py-2.5 text-right">Basic / Actual</th>
              <th className="px-3 py-2.5 text-right">GST</th>
              <th className="px-3 py-2.5 text-right">Gross</th>
            </tr>
          </thead>
          <tbody data-testid="reports-table-body">
            {loading ? (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400 italic">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400 italic">
                No inward records for the selected range
              </td></tr>
            ) : filtered.map((r, idx) => (
              <tr key={`${r.cost_center}|${r.supplier_name}|${r.category}|${r.part_no}|${r.part_name}`}
                  className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                  data-testid={`report-row-${idx}`}>
                <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">{idx + 1}</td>
                <td className="px-3 py-2 text-xs font-semibold text-[#1D2951]">{r.cost_center}</td>
                <td className="px-3 py-2 text-xs">{r.supplier_name}</td>
                <td className="px-3 py-2 text-xs">{r.category}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.part_no}</td>
                <td className="px-3 py-2 text-xs">{r.part_name}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmtINR(r.quantity)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">₹ {fmtINR(r.basic_value)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">₹ {fmtINR(r.gst_value)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-emerald-700">₹ {fmtINR(r.gross_value)}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-slate-100 font-semibold text-xs">
              <tr>
                <td colSpan={6} className="px-3 py-2.5 text-right uppercase tracking-wider text-slate-600">Totals</td>
                <td className="px-3 py-2.5 text-right font-mono">{fmtINR(totals.qty)}</td>
                <td className="px-3 py-2.5 text-right font-mono">₹ {fmtINR(totals.basic)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-slate-600">₹ {fmtINR(totals.gst)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-emerald-700">₹ {fmtINR(totals.gross)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
